import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { MobileBottomNav } from './MobileBottomNav';
import { AppErrorBoundary } from '@/components/shared/AppErrorBoundary';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { CmsShellHeader, CmsShellSidebar } from './cms/CmsShell';

/**
 * Which bundle this is, fixed at build time (see vite.config.ts inputs).
 *
 * A module-level constant, not an inline check: Vite replaces
 * import.meta.env.* with a literal, so Rollup can drop the dead branch and with
 * it the platform Sidebar/Header — and, importantly, CommandPalette and the
 * platform notification socket that Header would otherwise mount. The thirteen
 * CMS pages keep importing MainLayout unchanged.
 */
const IS_CMS_BUNDLE = import.meta.env.VITE_APP_VARIANT === 'cms';

interface MainLayoutProps {
  children: React.ReactNode;
  headerContent?: React.ReactNode;
  /**
   * Lets the page use the whole content area. Every other page is capped at
   * `max-w-page`; opt out only for canvases that lay themselves out by width
   * (dashboard grids, the case board).
   */
  fullWidth?: boolean;
}

export function MainLayout({ children, headerContent, fullWidth = false }: MainLayoutProps) {
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Owned here, not inside Sidebar: the toggle control lives in the Header
  // (beside the page title) and in the sidebar's own logo, so all three need
  // the same value and the same setter.
  const [collapsed, setCollapsed] = useState(false);
  const toggleCollapsed = () => setCollapsed((c) => !c);
  const location = useLocation();

  // Navigating from inside the drawer should close it — otherwise a tap on a
  // nav link leaves the overlay covering the page you just opened.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    // Plain page surface. The animated grid/glow belongs to the landing page
    // only; behind the app it showed through the header and made every theme
    // look different.
    <div className="flex h-screen overflow-hidden bg-background">

      {/* Desktop: the sidebar sits beside the content. On mobile it is pulled
          out of the flow and served from the drawer below, so the page gets the
          full width. */}
      {!isMobile && (IS_CMS_BUNDLE ? <CmsShellSidebar /> : <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} onSetCollapsed={setCollapsed} />)}

      <div className="flex flex-1 flex-col overflow-hidden">
        {IS_CMS_BUNDLE ? (
          <CmsShellHeader onOpenMobileNav={isMobile ? () => setDrawerOpen(true) : undefined}>
            {headerContent}
          </CmsShellHeader>
        ) : (
          <Header
            onOpenMobileNav={isMobile ? () => setDrawerOpen(true) : undefined}
            collapsed={collapsed}
            onToggleCollapsed={!isMobile ? toggleCollapsed : undefined}
          >
            {headerContent}
          </Header>
        )}
        {/* .page-width (index.css) caps the page's own root element rather
            than wrapping it, so pages that size themselves with h-full keep a
            definite parent height. */}
        <main className={cn('flex-1 overflow-y-auto pb-16 md:pb-0', !fullWidth && 'page-width')}>
          <AppErrorBoundary>{children}</AppErrorBoundary>
        </main>
      </div>

      {isMobile && (
        <>
          {/* MobileBottomNav links to /assets, /nirp/* and /admin/* — routes the
              CMS bundle does not ship, so it is drawer-only there. */}
          {!IS_CMS_BUNDLE && <MobileBottomNav onOpenMore={() => setDrawerOpen(true)} />}

          {/* The full navigation, as a left slide-in drawer wrapping the same
              Sidebar used on desktop — one source of nav truth for both. */}
          <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in" />
              <Dialog.Content
                aria-label="Navigation"
                className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left duration-300"
              >
                <Dialog.Title className="sr-only">Navigation</Dialog.Title>
                {IS_CMS_BUNDLE ? <CmsShellSidebar inDrawer /> : <Sidebar inDrawer />}
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </>
      )}
    </div>
  );
}
